/**
 * RecentlyWorkedOn - the distinct things you last touched (UI feedback D-6).
 *
 * The complement to Recent Activity: that is a chronology that repeats a
 * resource once per edit; this is the SET of resources - matters, reviews,
 * drafts, tables, research, projects - deduped and newest-first, capped at ten,
 * each a jump straight back to where you were. A gravatar sits on each row
 * because "what I worked on" is a personal list.
 */

import React from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  PiArrowClockwiseLight,
  PiBriefcaseLight,
  PiCheckSquareLight,
  PiClockCounterClockwiseLight,
  PiDatabaseLight,
  PiFileMagnifyingGlassLight,
  PiFileTextLight,
  PiKanbanLight,
  PiListMagnifyingGlassLight,
  PiPencilSimpleLineLight,
  PiTableLight,
} from 'react-icons/pi';
import { RootState } from '../../../store';
import { DashboardRecentlyWorkedOn } from '../../../store/reducer/DashboardReducer';
import GlassCard from '../../shared/brand/GlassCard';
import EmptyState from '../../shared/TailwindComponents/EmptyState';
import ProfileImage from '../../Header/ProfileImage';
import { feedTimeLabel } from '../../shared/feed/feedTime';

interface Props {
  items: DashboardRecentlyWorkedOn[];
}

/** entity_type (any casing) → glyph. */
const iconFor = (entityType: string): React.ReactNode => {
  const t = (entityType || '').toLowerCase();
  if (t.includes('matter')) return <PiBriefcaseLight />;
  if (t.includes('review')) return <PiFileMagnifyingGlassLight />;
  if (t.includes('table')) return <PiTableLight />;
  if (t.includes('research')) return <PiListMagnifyingGlassLight />;
  if (t.includes('draft')) return <PiPencilSimpleLineLight />;
  if (t.includes('template')) return <PiFileTextLight />;
  if (t.includes('datasource') || t.includes('data_source')) return <PiDatabaseLight />;
  if (t.includes('project')) return <PiKanbanLight />;
  if (t.includes('task')) return <PiCheckSquareLight />;
  return <PiClockCounterClockwiseLight />;
};

/** A readable type name for the meta line. */
const typeLabel = (entityType: string, t: (k: string) => string): string => {
  const key = (entityType || '').toLowerCase();
  const map: Record<string, string> = {
    matter: 'Matter', review: 'Review', reviewsession: 'Review', review_session: 'Review',
    table: 'Table', research: 'Research', draft: 'Draft', template: 'Template',
    datasource: 'Data source', data_source: 'Data source', project: 'Project', task: 'Task',
  };
  return t(map[key] || entityType || 'Item');
};

const RecentlyWorkedOn: React.FC<Props> = ({ items }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);

  const initials = `${userInfo?.FirstName?.[0] || ''}${userInfo?.LastName?.[0] || ''}`.toUpperCase();

  return (
    <GlassCard
      icon={<PiArrowClockwiseLight />}
      title={t('Recently worked on')}
      padding="none"
      className="overflow-hidden"
      fill
      bodyClassName="flex min-h-0 flex-1 flex-col"
      data-testid="recently-worked-on"
    >
      {items.length === 0 ? (
        <EmptyState
          density="compact"
          icon={<PiClockCounterClockwiseLight />}
          headline={t('Nothing yet')}
          subtext={t('The matters, reviews and other things you open will show up here.')}
        />
      ) : (
        <div className="custom-scrollbar-main flex min-h-0 flex-1 flex-col divide-y divide-hairline-soft overflow-y-auto">
          {items.slice(0, 10).map((item) => {
            const when = item.last_touched_at ? feedTimeLabel(item.last_touched_at, t) : null;
            const clickable = !!item.link;
            return (
              <button
                key={`${item.entity_type}-${item.entity_uuid}`}
                type="button"
                disabled={!clickable}
                onClick={() => clickable && history.push(item.link)}
                className="flex items-center gap-3 px-4 py-2.5 text-start transition-colors duration-200 ease-slide hover:bg-surface-sunken/70 focus:bg-surface-sunken/70 focus:outline-none disabled:cursor-default"
              >
                <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border border-hairline-soft bg-surface-sunken/70 text-fg-muted [&>svg]:h-4 [&>svg]:w-4">
                  {iconFor(item.entity_type)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-fg-body">
                    {item.label || typeLabel(item.entity_type, t)}
                  </span>
                  <span className="block truncate text-[11px] text-fg-muted">
                    {typeLabel(item.entity_type, t)}
                    {when?.label ? ` · ${when.label}` : ''}
                  </span>
                </span>
                <ProfileImage
                  profilePicture={userInfo?.profile_picture || userInfo?.ProfilePicture || null}
                  email={userInfo?.Email || ''}
                  initials={initials}
                  size="w-6 h-6"
                  className="flex-shrink-0"
                />
              </button>
            );
          })}
        </div>
      )}
    </GlassCard>
  );
};

export default RecentlyWorkedOn;
